import { type FC, useState, useEffect, useCallback, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { ModalContainer } from 'dibs-elements/exports/ModalContainer.tsx';
import { IntlProvider } from 'dibs-react-intl';
import serverVars from 'server-vars';
import styles from './styles/createOrWaitForChallenge.module.css';

const CHALLENGE_URL = '/dibs-fastly-js/challenge/';

const CHALLENGE_TIMEOUT_MS = 45000;

let activeChallengePromise: Promise<void> | null = null;

type Props = {
    onClose: () => void;
};

type ChallengeMessageEventNames =
    | 'challenge_init'
    | 'challenge_started'
    | 'captcha_prompted'
    | 'challenge_complete'
    | 'challenge_error';

type ChallengeMessageData = {
    eventType: 'fastly_captcha_challenge_event';
    eventName: ChallengeMessageEventNames;
};

function messageDataIsFromChallenge(
    message: MessageEvent
): message is MessageEvent<ChallengeMessageData> {
    return (
        message.origin === window.location.origin &&
        typeof message.data?.eventType === 'string' &&
        typeof message.data?.eventName === 'string' &&
        message.data.eventType === 'fastly_captcha_challenge_event'
    );
}

const ChallengeModal: FC<Props> = ({ onClose }) => {
    const [isOpen, setIsOpen] = useState(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const onRecieveMessage = useCallback(
        (message: MessageEvent<ChallengeMessageData | undefined>): void => {
            if (!messageDataIsFromChallenge(message)) {
                return;
            }
            if (message.data.eventName === 'captcha_prompted') {
                // don't timeout on the interactive captcha challenge
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                    timeoutRef.current = null;
                }
                setIsOpen(true);
            }
            if (['challenge_complete', 'challenge_error'].includes(message.data.eventName)) {
                // If challenge errors, close the modal. This will trigger a retry of the
                // request with a new challenge if needed
                onClose();
            }
        },
        [onClose, timeoutRef]
    );

    useEffect(() => {
        window.addEventListener('message', onRecieveMessage);
        return () => {
            window.removeEventListener('message', onRecieveMessage);
        };
    }, [onRecieveMessage]);

    useEffect(() => {
        timeoutRef.current = setTimeout(() => {
            onClose();
        }, CHALLENGE_TIMEOUT_MS);
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [onClose]);

    return (
        <ModalContainer renderWhileClosed isOpen={isOpen} onClose={() => {}}>
            <div className={styles.iframeWrapper}>
                <iframe className={styles.iframe} title="Security challenge" src={CHALLENGE_URL} />
            </div>
        </ModalContainer>
    );
};

export function createOrWaitForChallenge(): Promise<void> {
    if (activeChallengePromise) {
        return activeChallengePromise;
    }

    activeChallengePromise = new Promise(resolve => {
        const rootDiv = document.createElement('div');
        document.body.appendChild(rootDiv);
        const root = createRoot(rootDiv);

        function cleanup(): void {
            root.unmount();
            rootDiv.remove();
            activeChallengePromise = null;
            resolve();
        }

        root.render(
            <IntlProvider locale={serverVars.get('locale')} messages={serverVars.get('messages')}>
                <ChallengeModal onClose={cleanup} />
            </IntlProvider>
        );
    });

    return activeChallengePromise;
}
